// SessionGuard — single-active-device model (spec §11.8).
//
// On mount this component claims the active session for this browser instance,
// then polls /api/session/heartbeat every 5 s. If another device claims the
// session, the heartbeat returns {active: false} and a full-viewport overlay
// is shown. Clicking "Take over →" re-claims and dismisses the overlay.
// If the active device goes quiet for >20 s, the heartbeat auto-reclaims and
// the overlay dismisses itself — no manual action needed.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";

import "./SessionGuard.css";

const POLL_MS = 1_000;
const STORAGE_KEY = "ophelia_device_token";

function getDeviceToken(): string {
  let token = localStorage.getItem(STORAGE_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEY, token);
  }
  return token;
}

export function SessionGuard({ children }: { children: React.ReactNode }) {
  // null = claim in flight, true = active, false = displaced
  const [active, setActive] = useState<boolean | null>(null);
  const [claiming, setClaiming] = useState(false);
  const tokenRef = useRef(getDeviceToken());

  useEffect(() => {
    const token = tokenRef.current;

    // Claim on mount — makes this device the active one immediately.
    apiFetch("/api/session/claim", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then(() => setActive(true))
      .catch(() => setActive(true)); // network hiccup: optimistically active

    // Poll heartbeat to detect displacement or auto-recover stale sessions.
    const id = setInterval(async () => {
      try {
        const res = await apiFetch(
          `/api/session/heartbeat?token=${encodeURIComponent(token)}`,
        );
        if (res.ok) {
          const data = (await res.json()) as { active: boolean };
          setActive(data.active);
        }
      } catch {
        // Network error — keep current state; don't flicker the overlay.
      }
    }, POLL_MS);

    return () => clearInterval(id);
  }, []);

  async function takeOver() {
    setClaiming(true);
    try {
      await apiFetch("/api/session/claim", {
        method: "POST",
        body: JSON.stringify({ token: tokenRef.current }),
      });
      setActive(true);
    } catch {
      // Will retry on next poll.
    } finally {
      setClaiming(false);
    }
  }

  return (
    <>
      {children}
      {active === false && <DisplacedOverlay onTakeOver={takeOver} busy={claiming} />}
    </>
  );
}

function DisplacedOverlay({
  onTakeOver,
  busy,
}: {
  onTakeOver: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="session-overlay" role="dialog" aria-modal="true">
      <div className="session-overlay__panel">
        <div className="session-overlay__logo-row">
          <img src="/logo.svg" alt="" className="session-overlay__logo" />
          <span className="session-overlay__wordmark">ophelia</span>
        </div>
        <div className="session-overlay__body">
          <p className="session-overlay__title">{t("session.title")}</p>
          <p className="session-overlay__subtitle">{t("session.subtitle")}</p>
        </div>
        <button
          type="button"
          className="session-overlay__btn"
          onClick={onTakeOver}
          disabled={busy}
        >
          {t("session.take_over")}
        </button>
      </div>
    </div>
  );
}
